import { NgClass } from '@angular/common';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorNotificationComponent } from '@components/error-notification/error-notification.component';
import { TestCenterComponent } from '@components/test-center/test-center.component';
import { Sites } from '@enums/sites';
import { StorageKeys } from '@enums/storage-keys';
import { environment } from '@environments/environment';
import { BetterLabStyles } from '@interfaces/better-lab-styles';
import { Result, ResultService } from '@Medology/ng-findalab';
import { Center } from '@models/center';
import { DocumentService } from '@services/document.service';
import { OrderService } from '@services/order.service';
import { PartialResultsDeliveryService } from '@services/partial-results-delivery.service';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'app-order-better-lab',
  templateUrl: './order-better-lab.component.html',
  styleUrls: ['./order-better-lab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [NgClass, TestCenterComponent, ErrorNotificationComponent],
})
export class OrderBetterLabComponent implements OnInit {
  /**
   * Center stored in local storage
   */
  public center: Center = null;

  /**
   * Error message to display if lab update/keep process fails
   */
  public betterLabError: string = '';

  /**
   * The zip code of the lab with which the order was placed.
   */
  public orderLabZipCode: string = '';

  public hasError: boolean = false;

  private storage = inject(StorageService);
  private orderService = inject(OrderService);
  private route = inject(Router);
  private resultService = inject(ResultService);
  private partialResultsDeliveryService = inject(PartialResultsDeliveryService);
  private documentService = inject(DocumentService);

  public constructor() {
    this.storage.inHomeCollection = false;
  }

  /**
   * Get the styles for the better lab page based on the current site.
   */
  public get betterLabStyles(): BetterLabStyles {
    return environment.styles.orderBetterLab as BetterLabStyles;
  }

  /**
   * Whether the current site is STDCheck.
   */
  public get isStdCheck(): boolean {
    return (environment.site as Sites) === Sites.Stdcheck;
  }

  /**
   * Whether the current site is HealthLabs.
   */
  public get isHealthlabs(): boolean {
    return (environment.site as Sites) === Sites.Healthlabs;
  }

  /**
   * Gets the ordered tests from storage.
   */
  public get orderedTests() {
    return this.storage.orderedTests;
  }

  public ngOnInit(): void {
    this.orderService.labResultSubscription.unsubscribe();
    this.center = this.storage.center;
    this.orderLabZipCode = this.storage.order?.lab?.zip_code || '';
    this.resultService.resultSelected$.subscribe((result: Result) => this.onLabUpdate(result));
  }

  /**
   * Handle Keep Current lab
   */
  public onKeepCurrentLab(): void {
    this.storage.remove(StorageKeys.BetterLab);
    this.redirect();
  }

  /**
   * Select the Lab and redirect to the order page
   *
   * @param result
   */
  private onLabUpdate(result: Result): void {
    this.betterLabError = '';
    this.hasError = false;

    this.orderService.updateLab(result.id, this.storage.order.hash).subscribe({
      next: this.onSuccessLabUpdate.bind(this),
      error: this.onErrorLabUpdate.bind(this),
    });
  }

  /**
   * Handle the lab update success
   */
  private onSuccessLabUpdate(): void {
    this.storage.labChanged = 'yes';
    this.storage.remove(StorageKeys.BetterLab);
    this.redirect();
  }

  /**
   * Handle the lab update error
   * @param { HttpErrorResponse } error
   */
  private onErrorLabUpdate(error): void {
    this.hasError = true;
    this.betterLabError = error.error.message;
    this.documentService.scrollToBottom();
  }

  /**
   * Performs redirect.
   */
  private redirect(): void {
    if (this.partialResultsDeliveryService.shouldRedirectToPartialResultsDelivery()) {
      this.redirectPartialResultsDelivery();

      return;
    }

    this.redirectDoxyPep();
  }

  /**
   * Redirects to the DoxyPEP treatment page.
   */
  private redirectDoxyPep(): void {
    this.route.navigateByUrl('doxy-pep-treatment');
  }

  /**
   * Redirects to the Partial results delivery page.
   */
  private redirectPartialResultsDelivery(): void {
    this.route.navigateByUrl('partial-results-delivery');
  }
}
